/**
 * Traduction francais / anglais.
 *
 * Un dictionnaire plat, pas de bibliotheque : l'application tient en quelques
 * ecrans et chaque kilo-octet compte devant un module WASM de 2,7 Mo.
 *
 * Le type `Key` est derive du dictionnaire francais, qui fait donc reference :
 * TypeScript refuse de compiler si une cle manque a l'anglais ou si on demande
 * une cle qui n'existe pas. Une chaine oubliee est une erreur de compilation,
 * pas un « ??? » decouvert en production.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export type Lang = 'fr' | 'en';

const STORAGE = 'ps2-lang';

/* ---------------------------------------------------------------- francais */

const FR = {
	/* -- coque -- */
	'nav.library': 'Bibliothèque',
	'nav.compat': 'Compatibilité',
	'nav.tech': 'Technique',
	'nav.about': 'À propos',

	/* -- pied de page d'etat -- */
	'status.wasm': 'WASM',
	'status.wasm.ready': 'prêt',
	'status.wasm.absent': 'absent',
	'status.sab': 'SAB',
	'status.sab.isolated': 'isolé',
	'status.sab.missing': 'absent',
	'status.bios': 'BIOS',
	'status.bios.hle': 'HLE',
	'status.cores': 'CŒURS',
	'status.mem': 'MÉM',
	'status.pad': 'MANETTE',
	'status.pad.none': 'aucune',
	'status.session': 'session en pause',

	/* -- bibliotheque -- */
	'lib.title': 'Bibliothèque',
	'lib.empty.eyebrow': 'Émulateur PlayStation 2 · WebAssembly',
	'lib.empty.head1': 'Aucun jeu',
	'lib.empty.head2': 'chargé',
	'lib.drop': 'Dépose une image disque (.iso, .cso, .chd, .isz, .bin) sur cette page.',
	'lib.drop.note': 'Les fichiers restent sur ta machine — rien n’est téléversé.',
	'lib.add': 'Ajouter un fichier',
	'lib.add.short': '+ Ajouter',
	'lib.persist': 'les jeux ajoutés restent listés d’une visite à l’autre.',
	'lib.local': 'locaux',
	'lib.col.title': 'Titre',
	'lib.col.code': 'Code',
	'lib.col.region': 'Région',
	'lib.col.size': 'Taille',
	'lib.col.compat': 'Compatibilité',
	'lib.col.last': 'Dernière fois',
	'lib.paused': 'en pause',
	'lib.remove.hint': 'Clic droit pour retirer',
	'lib.game': 'jeu',
	'lib.games': 'jeux',

	/* -- jeu de demonstration -- */
	'demo.eyebrow': 'Essayer tout de suite',
	'demo.title': 'Doom',
	'demo.sub': 'Portage PlayStation 2 · 7 Mo',
	'demo.note': 'Aucun fichier à fournir : le jeu se lance depuis le site.',
	'demo.play': 'Lancer la démo',
	'demo.resume': 'Reprendre la démo',
	'demo.paused': 'en pause',
	'demo.compact': 'Pas d’image disque sous la main ?',
	'demo.compact.link': 'Lancer la démo Doom',
	'demo.tech': 'Comment ce portage a été fait',

	/* -- etats de compatibilite -- */
	'compat.playable': 'jouable',
	'compat.ingame': 'en jeu',
	'compat.loadable': 'charge',
	'compat.intro': 'intro seule',
	'compat.nothing': 'rien',
	'compat.untested': 'non testé',
	'compat.unlisted': 'non répertorié',
	'compat.unknown': '—',

	/* -- initialisation -- */
	'init.title': 'Initialisation',
	'init.module': 'Play.wasm · instanciation',
	'init.secure': 'contexte sécurisé (HTTPS)',
	'init.coop': 'COOP / COEP — crossOriginIsolated',
	'init.sab': 'SharedArrayBuffer disponible',
	'init.compile': 'compilation du module',
	'init.bios': 'amorçage BIOS HLE',
	'init.threads': 'ouverture des fils d’émulation',

	/* -- erreurs -- */
	'err.sab.eyebrow': 'Moteur indisponible',
	'err.sab.title': 'SharedArrayBuffer absent',
	'err.sab.body':
		'L’émulateur a besoin d’une page isolée entre origines. Trois conditions doivent être réunies :',
	'err.sab.coop': 'Cross-Origin-Opener-Policy: same-origin',
	'err.sab.coep': 'Cross-Origin-Embedder-Policy: require-corp',
	'err.sab.https': 'HTTPS avec certificat valide',
	'err.disc.eyebrow': 'Lecture impossible',
	'err.disc.title': 'Image disque illisible',
	'err.disc.formats': 'Formats acceptés : .iso, .cso, .chd, .isz, .bin.',
	'err.disc.retry': 'Choisir un autre fichier',
	'err.close': 'Fermer',

	/* -- vue de jeu -- */
	'game.back': '← Bibliothèque',
	'game.states': 'États',
	'game.settings': 'Réglages',
	'game.fullscreen': 'Plein écran',
	'game.pad': 'manette',
	'game.booting': 'Lecture du disque',
	'game.hud.hint': 'Approche le curseur du haut de l’écran pour afficher les commandes.',

	/* -- sauvegardes -- */
	'states.title': 'Sauvegardes',
	'states.slots': 'emplacements',
	'states.slot': 'Emplacement',
	'states.empty': 'vide',
	'states.save': 'Sauver',
	'states.overwrite': 'Écraser',
	'states.load': 'Charger',
	'states.export': 'Télécharger cet état',
	'states.import': 'Importer un fichier d’état',
	'states.delete': 'Supprimer',
	'states.note':
		'Stockées par le navigateur, sur ta machine. Vider les données du site les efface — l’export les met à l’abri.',
	'states.note2': 'Format identique à la version de bureau (.zip).',
	'states.saving': 'Sauvegarde',
	'states.loading': 'Chargement',

	/* -- reglages -- */
	'set.title': 'Réglages',
	'set.resolution': 'Résolution interne',
	'set.resolution.note':
		'Améliore la qualité, dépend de ta machine : essaie et garde ce qui te convient.',
	'set.input': 'Commandes',
	'set.input.auto': 'Auto',
	'set.input.keyboard': 'Clavier',
	'set.input.gamepad': 'Manette',
	'set.input.none':
		'Aucune manette détectée. Branche-la puis appuie sur un bouton — les navigateurs ne la révèlent qu’après une première action.',
	'set.input.active': 'Actif',
	'set.input.one': 'Un bouton ne peut recevoir qu’une source à la fois.',
	'set.keyboard': 'Clavier',
	'set.keyboard.off': 'Clavier — inactif en mode manette',
	'set.keyboard.capture': '— appuie sur une touche',
	'set.reset': 'Réinitialiser',
	'set.volume': 'Volume',
	'set.volume.note':
		'Appliqué dans le processeur sonore émulé, pas par le navigateur : le réglage survit au changement de jeu.',
	'set.volume.mute': 'Couper',
	'set.volume.unmute': 'Rétablir',

	/* -- panneau de statistiques -- */
	'stats.title': 'Statistiques',
	'stats.fps': 'Images par seconde',
	'stats.draws': 'Appels de dessin',
	'stats.draws.unit': 'par image',
	'stats.ee': 'Charge Emotion Engine',
	'stats.iop': 'Charge IOP',
	'stats.wasm': 'Mémoire de l’émulateur',
	'stats.heap': 'Tas JavaScript',
	'stats.res': 'Résolution interne',
	'stats.canvas': 'Surface de rendu',
	'stats.cores': 'Cœurs logiques',
	'stats.pad': 'Manette',
	'stats.hint': 'Approche le curseur du bas de l’écran pour les afficher.',
	'stats.unavailable': 'indisponible',
	'stats.heap.note': 'Chromium seulement — les autres navigateurs ne l’exposent pas.',

	/* -- tutoriel -- */
	'tut.active': 'Tuto actif',
	'tut.markers': 'repères',
	'tut.replay': 'Réafficher le tuto',
	'tut.first': 'Affiché à la première visite',
	'tut.dismiss': 'Terminer',
	'tut.add':
		'Apporte ta propre image disque. Elle est lue sur place par le navigateur, secteur par secteur : rien n’est téléversé, rien n’est copié.',
	'tut.nav':
		'Avant de chercher pourquoi un jeu ne démarre pas, vérifie son état ici : la moitié des blocages sont des limites connues de l’émulateur, référencées par code SLUS/SLES.',
	'tut.entry':
		'Clic pour lancer ou reprendre. Clic droit pour retirer l’entrée — le fichier d’origine reste intact sur ta machine.',
	'tut.ctrl':
		'Le HUD reste masqué pendant le jeu et réapparaît au survol du haut de l’écran : sauvegardes d’état, résolution interne, plein écran.',

	/* -- avertissement navigateur -- */
	'warn.firefox':
		'Firefox est jouable mais ralenti, et certains niveaux saturent la mémoire au chargement. Chrome donne de meilleurs résultats — détails dans',

	/* -- a propos (vue React) -- */
	'about.title': 'Un émulateur PlayStation 2 qui tourne dans le navigateur',
	'about.lede':
		'Pas d’installation, pas de téléchargement, pas de compte : l’émulation s’exécute dans l’onglet, sur votre machine.',
	'about.s1': 'Ce que c’est',
	'about.s2': 'Un fork de Play!',
	'about.s3': 'Pourquoi ce fork existe',
	'about.s4': 'Correctifs propres au navigateur',
	'about.s5': 'Ce que l’interface ajoute',
	'about.s6': 'Navigateurs',
	'about.s7': 'Limites connues',
	'about.p1':
		'Le cœur de l’émulateur est écrit en C++ et compilé en WebAssembly, un format que les navigateurs exécutent à une vitesse proche du code natif. Le processeur Emotion Engine de la PlayStation 2, son processeur d’entrées-sorties et son synthétiseur graphique sont simulés dans l’onglet ; le rendu passe par WebGL.',
	'about.p2':
		'Aucun serveur n’exécute le jeu. C’est votre machine qui travaille — ce qui explique que les performances dépendent de votre processeur, et qu’un ordinateur de bureau donne de bien meilleurs résultats qu’un téléphone.',
	'about.privacy.strong': 'Vos jeux ne quittent pas votre machine.',
	'about.privacy':
		'L’image disque que vous ajoutez est lue sur place, secteur par secteur, à travers l’API de fichiers du navigateur — exactement comme le ferait un lecteur. Elle n’est jamais téléversée, et le site ne conserve rien.',
	'about.fork':
		'Le cœur de l’émulation vient de Play!, de Jean-Philippe Desjardins, sous licence libre. Ce projet ne réécrit rien de l’émulation : il corrige trois défauts précis et remplace l’interface web.',
	'about.why':
		'Il est né d’un jeu qui ne fonctionnait pas — Aggressive Inline (PAL, SLES-504.80) — et de trois bugs trouvés en cherchant pourquoi.',
	'about.fix1.tag': 'CDVD',
	'about.fix1.strong': 'Verrou vivant à la lecture du disque.',
	'about.fix1':
		'Une commande de lecture renvoyait zéro pendant qu’une autre était en attente ; deux consommateurs simultanés se bloquaient mutuellement et le jeu ne démarrait jamais. Le défaut touche n’importe quel titre faisant des accès disque concurrents.',
	'about.fix2.tag': 'Vidéo',
	'about.fix2.strong': 'Cache de textures gelé par un transfert partiel.',
	'about.fix2':
		'Le jeu annonce une image de 512×256 puis en envoie 32 octets de moins. Sur console c’est sans effet. Ici, le cache n’était invalidé que si le compteur tombait pile à zéro — ce qui n’arrivait jamais. Les cinématiques restaient blanches.',
	'about.fix3.tag': 'Image',
	'about.fix3.strong': 'Tressaillement en rendu par trame.',
	'about.fix3':
		'Le jeu ne dessine qu’une trame sur deux et décale sa géométrie d’une demi-ligne pour qu’elles s’entrelacent. Un téléviseur les recombine ; un écran progressif affichait l’une puis l’autre, d’où une oscillation verticale à 50 Hz.',
	'about.fix.note':
		'Les deux premiers correctifs ne sont pas spécifiques à ce jeu et ont vocation à être proposés au projet d’origine.',
	'about.web':
		'Le portage web souffrait de trois défauts de synchronisation, invisibles sur la version de bureau. Chacun a été identifié à la mesure, pas au diagnostic :',
	'about.web1.tag': 'Disque',
	'about.web1.strong': 'Attente active à chaque lecture de secteur.',
	'about.web1':
		'Le fil d’émulation interrogeait le fil principal en boucle jusqu’à ce que la lecture aboutisse. Chaque tour coûtait une trame d’affichage complète : sur 86 secondes de session, 77 étaient passées à attendre. Remplacé par un réveil direct en mémoire partagée — l’attente est tombée à 1,7 s.',
	'about.web2.tag': 'Cadence',
	'about.web2.strong': 'Le limiteur d’images dérivait.',
	'about.web2':
		'Il raisonnait en durées de sommeil, si bien que chaque imprécision s’ajoutait à la suivante. L’émulation tournait à 54 images/s pour une cible de 50. Il vise désormais des échéances absolues, ce qui rend la cadence exacte.',
	'about.web3.tag': 'Son',
	'about.web3.strong': 'Décalage croissant entre l’image et le son.',
	'about.web3':
		'Conséquence du précédent : le processeur sonore produit ses échantillons au rythme des cycles émulés, donc 8 % d’images en trop donnaient 80 ms de son excédentaire par seconde. Le retard atteignait plusieurs secondes. La cadence corrigée, il a disparu.',
	'about.ui':
		'Bibliothèque persistante, sauvegardes d’état interchangeables avec la version de bureau, reprise de session, choix de la résolution interne, manette, remappage du clavier et plein écran — autant de réglages qui existaient dans le cœur de l’émulateur mais n’étaient pas atteignables depuis le navigateur.',
	'about.chrome.strong': 'Chrome et dérivés',
	'about.chrome': 'pleinement fonctionnel.',
	'about.firefox.strong': 'Firefox',
	'about.firefox':
		'jouable, mais deux défauts subsistent : un ralentissement résiduel, et une saturation mémoire au chargement de certains niveaux. La cause est identifiée — le compilateur à la volée crée un module WebAssembly par bloc de code traduit, sans jamais les recycler — mais la corriger demande de toucher au cœur du moteur, ce qui n’a pas été fait.',
	'about.lim1': 'Le décodage vidéo reste nettement plus lent qu’en natif.',
	'about.lim2':
		'La résolution interne monte jusqu’à 16×. Le coût dépend entièrement de la machine : mieux vaut essayer que supposer.',
	'about.lim3':
		'Les sauvegardes d’état sont stockées par le navigateur. Vider ses données de site les efface — le bouton d’export permet de les mettre à l’abri.',
	'about.lim4': 'Aucun jeu n’est fourni : il faut apporter ses propres images disque.',
	'about.req': 'Ce qu’il vous faut',
	'about.req.browser': 'Navigateur',
	'about.req.browser.v': 'Chrome, Edge, Firefox ou Safari à jour, sur ordinateur de préférence',
	'about.req.tech': 'Technologies',
	'about.req.tech.v': 'WebAssembly et SharedArrayBuffer (activés par défaut)',
	'about.req.conn': 'Connexion',
	'about.req.conn.v':
		'HTTPS obligatoire — la mémoire partagée n’est accessible qu’aux pages sécurisées',
	'about.req.game': 'Le jeu',
	'about.req.game.v': 'Votre propre image disque, au format ISO, d’un jeu que vous possédez',

	/* -- divers -- */
	'time.never': 'jamais lancé',
	'time.now': 'à l’instant',
	'time.min': 'il y a {n} min',
	'time.hour': 'il y a {n} h',
	'time.yesterday': 'hier',
	'time.day': 'il y a {n} j',
	'size.gb': 'Go',
	'size.mb': 'Mo',
	'unit.fps': 'f/s',
};

export type Key = keyof typeof FR;

/* ----------------------------------------------------------------- anglais */

const EN: Record<Key, string> = {
	'nav.library': 'Library',
	'nav.compat': 'Compatibility',
	'nav.tech': 'Technical',
	'nav.about': 'About',

	'status.wasm': 'WASM',
	'status.wasm.ready': 'ready',
	'status.wasm.absent': 'absent',
	'status.sab': 'SAB',
	'status.sab.isolated': 'isolated',
	'status.sab.missing': 'missing',
	'status.bios': 'BIOS',
	'status.bios.hle': 'HLE',
	'status.cores': 'CORES',
	'status.mem': 'MEM',
	'status.pad': 'GAMEPAD',
	'status.pad.none': 'none',
	'status.session': 'session paused',

	'lib.title': 'Library',
	'lib.empty.eyebrow': 'PlayStation 2 emulator · WebAssembly',
	'lib.empty.head1': 'No game',
	'lib.empty.head2': 'loaded',
	'lib.drop': 'Drop a disc image (.iso, .cso, .chd, .isz, .bin) onto this page.',
	'lib.drop.note': 'Files stay on your machine — nothing is uploaded.',
	'lib.add': 'Add a file',
	'lib.add.short': '+ Add',
	'lib.persist': 'added games stay listed from one visit to the next.',
	'lib.local': 'local',
	'lib.col.title': 'Title',
	'lib.col.code': 'Code',
	'lib.col.region': 'Region',
	'lib.col.size': 'Size',
	'lib.col.compat': 'Compatibility',
	'lib.col.last': 'Last played',
	'lib.paused': 'paused',
	'lib.remove.hint': 'Right-click to remove',
	'lib.game': 'game',
	'lib.games': 'games',

	'demo.eyebrow': 'Try it right now',
	'demo.title': 'Doom',
	'demo.sub': 'PlayStation 2 port · 7 MB',
	'demo.note': 'No file needed — the game is served by this site.',
	'demo.play': 'Play the demo',
	'demo.resume': 'Resume the demo',
	'demo.paused': 'paused',
	'demo.compact': 'No disc image at hand?',
	'demo.compact.link': 'Play the Doom demo',
	'demo.tech': 'How this port was made',

	'compat.playable': 'playable',
	'compat.ingame': 'in-game',
	'compat.loadable': 'loadable',
	'compat.intro': 'intro only',
	'compat.nothing': 'nothing',
	'compat.untested': 'untested',
	'compat.unlisted': 'unlisted',
	'compat.unknown': '—',

	'init.title': 'Initialising',
	'init.module': 'Play.wasm · instantiation',
	'init.secure': 'secure context (HTTPS)',
	'init.coop': 'COOP / COEP — crossOriginIsolated',
	'init.sab': 'SharedArrayBuffer available',
	'init.compile': 'compiling the module',
	'init.bios': 'HLE BIOS boot',
	'init.threads': 'starting emulation threads',

	'err.sab.eyebrow': 'Engine unavailable',
	'err.sab.title': 'SharedArrayBuffer missing',
	'err.sab.body':
		'The emulator needs a cross-origin isolated page. Three conditions must all be met:',
	'err.sab.coop': 'Cross-Origin-Opener-Policy: same-origin',
	'err.sab.coep': 'Cross-Origin-Embedder-Policy: require-corp',
	'err.sab.https': 'HTTPS with a valid certificate',
	'err.disc.eyebrow': 'Cannot read',
	'err.disc.title': 'Unreadable disc image',
	'err.disc.formats': 'Accepted formats: .iso, .cso, .chd, .isz, .bin.',
	'err.disc.retry': 'Choose another file',
	'err.close': 'Close',

	'game.back': '← Library',
	'game.states': 'States',
	'game.settings': 'Settings',
	'game.fullscreen': 'Fullscreen',
	'game.pad': 'gamepad',
	'game.booting': 'Reading the disc',
	'game.hud.hint': 'Move the cursor to the top of the screen to show the controls.',

	'states.title': 'Save states',
	'states.slots': 'slots',
	'states.slot': 'Slot',
	'states.empty': 'empty',
	'states.save': 'Save',
	'states.overwrite': 'Overwrite',
	'states.load': 'Load',
	'states.export': 'Download this state',
	'states.import': 'Import a state file',
	'states.delete': 'Delete',
	'states.note':
		'Stored by the browser, on your machine. Clearing site data erases them — export puts them out of harm’s way.',
	'states.note2': 'Same format as the desktop build (.zip).',
	'states.saving': 'Saving',
	'states.loading': 'Loading',

	'set.title': 'Settings',
	'set.resolution': 'Internal resolution',
	'set.resolution.note':
		'Improves quality, depends on your machine: try it and keep what suits you.',
	'set.input': 'Controls',
	'set.input.auto': 'Auto',
	'set.input.keyboard': 'Keyboard',
	'set.input.gamepad': 'Gamepad',
	'set.input.none':
		'No gamepad detected. Plug it in then press a button — browsers only reveal it after a first action.',
	'set.input.active': 'Active',
	'set.input.one': 'A button can only take one source at a time.',
	'set.keyboard': 'Keyboard',
	'set.keyboard.off': 'Keyboard — inactive in gamepad mode',
	'set.keyboard.capture': '— press a key',
	'set.reset': 'Reset',
	'set.volume': 'Volume',
	'set.volume.note':
		'Applied inside the emulated sound processor, not by the browser: the setting survives a change of game.',
	'set.volume.mute': 'Mute',
	'set.volume.unmute': 'Unmute',

	'stats.title': 'Statistics',
	'stats.fps': 'Frames per second',
	'stats.draws': 'Draw calls',
	'stats.draws.unit': 'per frame',
	'stats.ee': 'Emotion Engine load',
	'stats.iop': 'IOP load',
	'stats.wasm': 'Emulator memory',
	'stats.heap': 'JavaScript heap',
	'stats.res': 'Internal resolution',
	'stats.canvas': 'Render surface',
	'stats.cores': 'Logical cores',
	'stats.pad': 'Gamepad',
	'stats.hint': 'Move the cursor to the bottom of the screen to show them.',
	'stats.unavailable': 'unavailable',
	'stats.heap.note': 'Chromium only — other browsers do not expose it.',

	'tut.active': 'Tour active',
	'tut.markers': 'markers',
	'tut.replay': 'Show the tour again',
	'tut.first': 'Shown on your first visit',
	'tut.dismiss': 'Done',
	'tut.add':
		'Bring your own disc image. The browser reads it in place, sector by sector: nothing is uploaded, nothing is copied.',
	'tut.nav':
		'Before wondering why a game won’t start, check its status here: half of all stalls are known emulator limits, listed by SLUS/SLES code.',
	'tut.entry':
		'Click to start or resume. Right-click to remove the entry — the original file stays untouched on your machine.',
	'tut.ctrl':
		'The HUD stays hidden while you play and returns when you hover the top of the screen: save states, internal resolution, fullscreen.',

	'warn.firefox':
		'Firefox is playable but slower, and some levels exhaust memory while loading. Chrome gives better results — details in',

	'about.title': 'A PlayStation 2 emulator that runs in the browser',
	'about.lede':
		'No install, no download, no account: emulation runs in the tab, on your own machine.',
	'about.s1': 'What this is',
	'about.s2': 'A fork of Play!',
	'about.s3': 'Why this fork exists',
	'about.s4': 'Browser-specific fixes',
	'about.s5': 'What the interface adds',
	'about.s6': 'Browsers',
	'about.s7': 'Known limits',
	'about.p1':
		'The emulator core is written in C++ and compiled to WebAssembly, a format browsers run at close to native speed. The PlayStation 2’s Emotion Engine, its I/O processor and its graphics synthesiser are all simulated in the tab; rendering goes through WebGL.',
	'about.p2':
		'No server runs the game. Your machine does the work — which is why performance depends on your processor, and why a desktop computer does far better than a phone.',
	'about.privacy.strong': 'Your games never leave your machine.',
	'about.privacy':
		'The disc image you add is read in place, sector by sector, through the browser’s file API — exactly as a drive would. It is never uploaded, and the site keeps nothing.',
	'about.fork':
		'The emulation core comes from Play!, by Jean-Philippe Desjardins, under a free licence. This project rewrites none of the emulation: it fixes three specific defects and replaces the web interface.',
	'about.why':
		'It started with a game that would not run — Aggressive Inline (PAL, SLES-504.80) — and three bugs found while working out why.',
	'about.fix1.tag': 'CDVD',
	'about.fix1.strong': 'Livelock while reading the disc.',
	'about.fix1':
		'One read command returned zero while another was still pending; two simultaneous consumers blocked each other and the game never started. The defect affects any title issuing concurrent disc reads.',
	'about.fix2.tag': 'Video',
	'about.fix2.strong': 'Texture cache frozen by a partial transfer.',
	'about.fix2':
		'The game announces a 512×256 image then sends 32 bytes fewer. On console this has no effect. Here the cache was only invalidated if the counter landed exactly on zero — which never happened. Cutscenes stayed white.',
	'about.fix3.tag': 'Picture',
	'about.fix3.strong': 'Judder in field rendering.',
	'about.fix3':
		'The game draws every other field and offsets its geometry by half a line so the fields interlace. A television recombines them; a progressive display showed one then the other, producing a 50 Hz vertical wobble.',
	'about.fix.note':
		'The first two fixes are not specific to this game and are meant to be offered upstream.',
	'about.web':
		'The web port suffered from three synchronisation defects, invisible on the desktop build. Each was found by measurement, not by diagnosis:',
	'about.web1.tag': 'Disc',
	'about.web1.strong': 'Busy-waiting on every sector read.',
	'about.web1':
		'The emulation thread polled the main thread in a loop until the read completed. Each round cost a full display frame: out of 86 seconds of session, 77 were spent waiting. Replaced by a direct wake-up in shared memory — waiting fell to 1.7 s.',
	'about.web2.tag': 'Pacing',
	'about.web2.strong': 'The frame limiter drifted.',
	'about.web2':
		'It reasoned in sleep durations, so every imprecision compounded. Emulation ran at 54 frames/s against a 50 target. It now aims at absolute deadlines, which makes the pacing exact.',
	'about.web3.tag': 'Sound',
	'about.web3.strong': 'Growing drift between picture and sound.',
	'about.web3':
		'A consequence of the previous one: the sound processor produces its samples at the rate of emulated cycles, so 8 % too many frames gave 80 ms of excess audio per second. The lag reached several seconds. With the pacing fixed, it vanished.',
	'about.ui':
		'Persistent library, save states interchangeable with the desktop build, session resume, internal resolution, gamepad, key remapping and fullscreen — settings that all existed in the emulator core but could not be reached from the browser.',
	'about.chrome.strong': 'Chrome and derivatives',
	'about.chrome': 'fully functional.',
	'about.firefox.strong': 'Firefox',
	'about.firefox':
		'playable, but two defects remain: residual slowdown, and memory exhaustion while loading certain levels. The cause is known — the just-in-time compiler creates one WebAssembly module per translated code block and never recycles them — but fixing it means touching the engine core, which has not been done.',
	'about.lim1': 'Video decoding remains markedly slower than native.',
	'about.lim2':
		'Internal resolution goes up to 16×. The cost depends entirely on the machine: better to try than to assume.',
	'about.lim3':
		'Save states are stored by the browser. Clearing site data erases them — the export button puts them somewhere safe.',
	'about.lim4': 'No game is provided: you bring your own disc images.',
	'about.req': 'What you need',
	'about.req.browser': 'Browser',
	'about.req.browser.v': 'An up-to-date Chrome, Edge, Firefox or Safari, preferably on a desktop',
	'about.req.tech': 'Technologies',
	'about.req.tech.v': 'WebAssembly and SharedArrayBuffer (enabled by default)',
	'about.req.conn': 'Connection',
	'about.req.conn.v': 'HTTPS required — shared memory is only available to secure pages',
	'about.req.game': 'The game',
	'about.req.game.v': 'Your own disc image, in ISO format, of a game you own',

	'time.never': 'never played',
	'time.now': 'just now',
	'time.min': '{n} min ago',
	'time.hour': '{n} h ago',
	'time.yesterday': 'yesterday',
	'time.day': '{n} d ago',
	'size.gb': 'GB',
	'size.mb': 'MB',
	'unit.fps': 'fps',
};

const DICTS: Record<Lang, Record<Key, string>> = { fr: FR, en: EN };

/* ------------------------------------------------------------------ reglage */

/**
 * Langue initiale : le choix explicite prime, sinon celle du navigateur.
 * Tout ce qui n'est pas francais retombe sur l'anglais — pour un visiteur
 * allemand ou espagnol, l'anglais est un bien meilleur defaut que le francais.
 */
function initialLang(): Lang {
	try {
		const stored = localStorage.getItem(STORAGE);
		if (stored === 'fr' || stored === 'en') return stored;
	} catch {
		/* stockage indisponible */
	}
	const nav = typeof navigator !== 'undefined' ? navigator.language : '';
	return nav.toLowerCase().startsWith('fr') ? 'fr' : 'en';
}

interface Ctx {
	lang: Lang;
	setLang: (lang: Lang) => void;
	t: (key: Key, vars?: Record<string, string | number>) => string;
}

const LangContext = createContext<Ctx | null>(null);

export function LangProvider({ children }: { children: React.ReactNode }) {
	const [lang, setLangState] = useState<Lang>(initialLang);

	// L'attribut lang de <html> doit suivre : il gouverne la cesure, la
	// synthese vocale et ce que les moteurs comprennent de la page.
	useEffect(() => {
		document.documentElement.lang = lang;
	}, [lang]);

	const setLang = useCallback((next: Lang) => {
		setLangState(next);
		try {
			localStorage.setItem(STORAGE, next);
		} catch {
			/* stockage indisponible */
		}
	}, []);

	const t = useCallback(
		(key: Key, vars?: Record<string, string | number>) => {
			let text = DICTS[lang][key] ?? DICTS.fr[key] ?? key;
			if (vars) {
				for (const [name, value] of Object.entries(vars)) {
					text = text.replace(`{${name}}`, String(value));
				}
			}
			return text;
		},
		[lang],
	);

	const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
	return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export function useLang(): Ctx {
	const ctx = useContext(LangContext);
	if (!ctx) throw new Error('useLang hors de LangProvider');
	return ctx;
}

/* -------------------------------------------------------------- formatage */

/**
 * Taille de fichier. Le separateur decimal suit la langue : virgule en
 * francais, point en anglais — ecrire « 1,4 GB » a un anglophone serait aussi
 * fautif que « 1.4 Go » a un francophone.
 */
export function formatSizeI18n(bytes: number, lang: Lang, t: (k: Key) => string): string {
	if (!bytes) return '—';
	const giga = bytes / 1073741824;
	if (giga >= 1) {
		const value = giga.toFixed(2);
		return `${lang === 'fr' ? value.replace('.', ',') : value} ${t('size.gb')}`;
	}
	return `${Math.round(bytes / 1048576)} ${t('size.mb')}`;
}

export function formatLastPlayedI18n(
	timestamp: number | null,
	lang: Lang,
	t: (k: Key, vars?: Record<string, string | number>) => string,
): string {
	if (!timestamp) return t('time.never');
	const elapsed = Date.now() - timestamp;
	const minutes = Math.floor(elapsed / 60000);
	if (minutes < 1) return t('time.now');
	if (minutes < 60) return t('time.min', { n: minutes });
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return t('time.hour', { n: hours });
	const days = Math.floor(hours / 24);
	if (days === 1) return t('time.yesterday');
	if (days < 30) return t('time.day', { n: days });
	return new Date(timestamp).toLocaleDateString(lang === 'fr' ? 'fr-FR' : 'en-GB');
}
